import * as React from "react";
import { ChevronDown, LogOut, Monitor, Moon, Sun } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DEFAULT_UI_SCALE,
  ThemePref,
  UI_SCALE_STEPS,
  usePreferences
} from "@/lib/preferences";
import { cn } from "@/lib/utils";
import { PHOTO_URL } from "../api";
import { AuthUser } from "../types";

const THEMES: { value: ThemePref; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor }
];

function initialsOf(user: AuthUser): string {
  return (user.name || user.email)
    .split(/[\s@.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function UserAvatar({ initials }: { initials: string }) {
  return (
    <Avatar className="h-8 w-8 shrink-0">
      <AvatarImage src={PHOTO_URL} alt="" />
      <AvatarFallback className="text-xs">{initials}</AvatarFallback>
    </Avatar>
  );
}

const sectionLabel = "text-xs font-medium uppercase tracking-wide text-muted-foreground";

/** Header avatar menu: profile, theme, text & icon size, sign out. A Popover
 *  rather than a DropdownMenu - Radix's menu takes over arrow keys for item
 *  navigation and closes on select, both of which fight the size slider. */
export function ProfileMenu({
  user,
  onLogout,
  className
}: {
  user: AuthUser;
  onLogout: () => void;
  className?: string;
}) {
  const { theme, setTheme, uiScale, setUiScale } = usePreferences();
  const [open, setOpen] = React.useState(false);
  const [confirmLogout, setConfirmLogout] = React.useState(false);
  const initials = initialsOf(user);
  const scaleIndex = Math.max(0, UI_SCALE_STEPS.indexOf(uiScale as (typeof UI_SCALE_STEPS)[number]));
  const percent = `${Math.round(uiScale * 100)}%`;
  const isDefault = uiScale === DEFAULT_UI_SCALE;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={cn("h-10 shrink-0 gap-2 rounded-full px-1 lg:pr-2.5", className)}
            aria-label="Profile and display settings"
          >
            <UserAvatar initials={initials} />
            <span className="hidden max-w-[12rem] truncate text-sm font-medium lg:inline">
              {user.name || user.email}
            </span>
            <ChevronDown className="hidden h-4 w-4 text-muted-foreground lg:inline" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={6} className="w-72 p-0">
          <div className="flex items-center gap-2.5 p-3">
            <UserAvatar initials={initials} />
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-sm font-medium">{user.name || user.email}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              {user.jobTitle && (
                <p className="truncate text-xs text-muted-foreground">{user.jobTitle}</p>
              )}
            </div>
          </div>
          <Separator />

          <div className="space-y-2 p-3">
            <p className={sectionLabel}>Theme</p>
            <ToggleGroup
              type="single"
              variant="outline"
              value={theme}
              onValueChange={(v) => v && setTheme(v as ThemePref)}
              className="grid grid-cols-3 gap-2"
            >
              {THEMES.map(({ value, label, icon: Icon }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  aria-label={`${label} theme`}
                  className="h-auto flex-col gap-1 py-2 text-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary"
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
          <Separator />

          <div className="space-y-3 p-3">
            <div className="flex items-center justify-between">
              <p className={sectionLabel}>Text &amp; icon size</p>
              <span className="text-xs font-medium tabular-nums">{percent}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-xs font-semibold"
                disabled={scaleIndex === 0}
                onClick={() => setUiScale(UI_SCALE_STEPS[scaleIndex - 1])}
                aria-label="Decrease text and icon size"
              >
                A
              </Button>
              <Slider
                min={0}
                max={UI_SCALE_STEPS.length - 1}
                step={1}
                value={[scaleIndex]}
                onValueChange={([i]) => setUiScale(UI_SCALE_STEPS[i])}
                aria-label="Text and icon size"
                aria-valuetext={percent}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-lg font-semibold"
                disabled={scaleIndex === UI_SCALE_STEPS.length - 1}
                onClick={() => setUiScale(UI_SCALE_STEPS[scaleIndex + 1])}
                aria-label="Increase text and icon size"
              >
                A
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={isDefault}
              onClick={() => setUiScale(DEFAULT_UI_SCALE)}
            >
              Resize to default
            </Button>
          </div>
          <Separator />

          <div className="p-1.5">
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 font-normal text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                setOpen(false);
                setConfirmLogout(true);
              }}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <ConfirmDialog
        open={confirmLogout}
        onOpenChange={setConfirmLogout}
        title="Sign out?"
        description="You'll need to sign in again to continue."
        confirmLabel="Sign out"
        destructive
        onConfirm={() => {
          setConfirmLogout(false);
          onLogout();
        }}
      />
    </>
  );
}
